import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';

export default function PaymentStatus() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: 'polling', message: 'Checking payment status…' });
  const attemptRef = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(loc.search).get('session_id');
    if (!sessionId) {
      setState({ status: 'error', message: 'Missing session id.' });
      return;
    }
    const poll = async () => {
      attemptRef.current += 1;
      if (attemptRef.current > 12) {
        setState({ status: 'error', message: 'Payment status check timed out. You can return to pricing and try again.' });
        return;
      }
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          await refresh();
          setState({ status: 'paid', message: 'Payment successful — your account is now Researcher tier.' });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', message: 'Checkout session expired. Please try again.' });
          return;
        }
        setState({ status: 'polling', message: 'Payment is being processed…' });
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: 'error', message: e.response?.data?.detail || 'Status check failed.' });
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const Icon = state.status === 'paid' ? CheckCircle2 : state.status === 'polling' ? Clock : XCircle;
  const iconColor = state.status === 'paid' ? 'text-[color:var(--success)]' : state.status === 'polling' ? 'text-[color:var(--accent-teal)]' : 'text-[color:var(--danger)]';

  return (
    <div className="max-w-lg mx-auto">
      <Card className="p-8 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] text-center">
        <div className={`mx-auto h-12 w-12 ${iconColor}`}><Icon className="h-12 w-12" /></div>
        <h1 className="mt-4 text-xl font-semibold tracking-tight">{state.status === 'paid' ? 'Welcome to Researcher tier' : state.status === 'polling' ? 'Verifying payment' : 'Checkout result'}</h1>
        <p className="mt-2 text-sm text-[color:var(--muted-fg)]">{state.message}</p>
        <div className="mt-6 flex justify-center gap-2">
          {state.status === 'paid' ? (
            <>
              <Button onClick={()=>nav('/dashboard')} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Go to dashboard</Button>
              <Button variant="outline" onClick={()=>nav('/match')}>Run a match</Button>
            </>
          ) : state.status === 'polling' ? null : (
            <Button onClick={()=>nav('/pricing')} variant="outline">Back to pricing</Button>
          )}
        </div>
      </Card>
    </div>
  );
}
